import { useState, type ReactNode } from 'react';
import type {
  AssistantPartnerSummary,
  AssistantUIMessage,
  GetBusinessPartnerOrdersOutput,
  GetBusinessPartnerOutput,
  GetItemOutput,
  GetOrderOutput,
  ListBusinessPartnerContactsOutput,
  QueryBusinessPartnersOutput,
  SearchItemsOutput,
} from '@/api/ai/ai.types';
import { formatMoneyString } from '@/utils/format-money-string';

/**
 * Renders a finished backend-tool call as a rich result card — so the
 * assistant never has to spell catalog, partner or order data out as a
 * Markdown table. One card per tool, dispatched on the typed part name.
 */

/** The chat message tool parts this component knows how to render. */
type ResultPart = Extract<
  AssistantUIMessage['parts'][number],
  { type: `tool-${string}` }
>;

const fmtEUR = (value: number | null | undefined): string =>
  value == null ? '—' : formatMoneyString(value);

const fmtInt = (value: number | null | undefined): string =>
  value == null ? '—' : value.toLocaleString('de-DE');

const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('de-DE');
};

/** Formats a day count as a relative German recency label. */
const fmtRelDays = (days: number | null | undefined): string => {
  if (days == null) return 'noch nie';
  if (days <= 0) return 'heute';
  if (days === 1) return 'vor 1 Tag';
  return `vor ${fmtInt(days)} Tagen`;
};

/** Maps the SAP partner-type code to a German label. */
const partnerTypeLabel = (type: string): string =>
  type === 'cCustomer'
    ? 'Kunde'
    : type === 'cSupplier'
      ? 'Lieferant'
      : type === 'cLid'
        ? 'Interessent'
        : type;

/** Normalises a partner website value into an absolute URL. */
const websiteHref = (website: string): string =>
  website.startsWith('http') ? website : `https://${website}`;

/** Builds a favicon URL for a partner website (Google S2, as used in BP detail). */
const faviconUrl = (website: string | null | undefined): string | null => {
  if (!website) return null;
  try {
    const domain = new URL(websiteHref(website)).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
  } catch {
    return null;
  }
};

function Card({ children }: { children: ReactNode }) {
  return <div className="eos-asst-rc">{children}</div>;
}

function CardHead({ title, meta }: { title: ReactNode; meta?: ReactNode }) {
  return (
    <div className="eos-asst-rc-head">
      <span className="eos-asst-rc-title">{title}</span>
      {meta != null && <span className="eos-asst-rc-meta">{meta}</span>}
    </div>
  );
}

function Missing({ text }: { text: string }) {
  return <div className="eos-asst-rc is-missing">{text}</div>;
}

function StatusPill({ status }: { status: 'open' | 'closed' }) {
  return (
    <span className={`eos-asst-rc-pill is-${status}`}>
      {status === 'open' ? 'Offen' : 'Abgeschlossen'}
    </span>
  );
}

/** overdueRatio at/above which a partner is shown as overdue (else on track). */
const OVERDUE_PILL_MIN = 1.3;
/** overdueRatio above which a partner has effectively churned (red, not amber). */
const CHURNED_RATIO = 5;

/** Badge for a partner overdue against their own ordering rhythm. */
function OverduePill({ ratio }: { ratio: number }) {
  const churned = ratio > CHURNED_RATIO;
  const label = ratio.toLocaleString('de-DE', { maximumFractionDigits: 1 });
  return (
    <span
      className={`eos-asst-rc-pill ${churned ? 'is-churned' : 'is-overdue'}`}
    >
      {`${label}× überfällig`}
    </span>
  );
}

/** When collapsed, the list previews this many rows behind a fade-out. */
const PREVIEW_ROWS = 4;

/**
 * Renders the result rows with a collapsed-by-default preview: the first
 * {@link PREVIEW_ROWS} rows are shown behind a fade so the user gets a taste
 * of the data without committing to the full list. The toggle button below
 * expands to the full set (or collapses back).
 */
function CollapsibleList<T>({
  items,
  renderItem,
  showLabel,
  hideLabel,
  className = 'eos-asst-rc-list',
}: {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  /** Label when collapsed, e.g. `(12) => 'Alle 12 Ansprechpartner anzeigen'`. */
  showLabel: (count: number) => string;
  /** Label when expanded, e.g. `(12) => '12 Ansprechpartner ausblenden'`. */
  hideLabel: (count: number) => string;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return null;
  const hasMore = items.length > PREVIEW_ROWS;
  const visibleItems = expanded ? items : items.slice(0, PREVIEW_ROWS);
  const showToggle = expanded || hasMore;
  const listClassName =
    !expanded && hasMore ? `${className} is-preview` : className;
  return (
    <>
      <ul className={listClassName}>{visibleItems.map(renderItem)}</ul>
      {showToggle && (
        <button
          type="button"
          className="eos-asst-rc-more"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span className="eos-asst-rc-more-caret" aria-hidden>
            {expanded ? '▾' : '▸'}
          </span>
          {expanded ? hideLabel(items.length) : showLabel(items.length)}
        </button>
      )}
    </>
  );
}

function Kv({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="eos-asst-rc-kv">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function ItemListCard({ output }: { output: SearchItemsOutput }) {
  if (output.count === 0) {
    return <Missing text={`Keine Artikel zu „${output.query}“ gefunden.`} />;
  }
  // Surface items the user can actually order — best stock first, unknown last.
  const sortedItems = [...output.items].sort((a, b) => {
    const aq = a.availableQuantity;
    const bq = b.availableQuantity;
    if (aq == null && bq == null) return 0;
    if (aq == null) return 1;
    if (bq == null) return -1;
    return bq - aq;
  });
  return (
    <Card>
      <CardHead
        title={`${output.count} Artikel`}
        meta={`„${output.query}“`}
      />
      <CollapsibleList
        items={sortedItems}
        showLabel={(n) => `Alle ${fmtInt(n)} Artikel anzeigen`}
        hideLabel={(n) => `${fmtInt(n)} Artikel ausblenden`}
        renderItem={(item) => (
          <li key={item.id} className="eos-asst-rc-row">
            <span className="eos-asst-rc-row-main">
              <span className="eos-asst-rc-row-name">{item.name}</span>
              <span className="eos-asst-rc-row-sub">
                <span className="eos-asst-mono">{item.id}</span>
                {item.manufacturer ? ` · ${item.manufacturer}` : ''}
                {item.itemGroup ? ` · ${item.itemGroup}` : ''}
              </span>
            </span>
            <span className="eos-asst-rc-row-side">
              <span className="eos-asst-mono">{fmtEUR(item.price)}</span>
              <span className="eos-asst-rc-row-meta">
                {fmtInt(item.availableQuantity)} verf.
              </span>
            </span>
          </li>
        )}
      />
    </Card>
  );
}

function ItemDetailCard({ output }: { output: GetItemOutput }) {
  if (!output.found) {
    return <Missing text={`Artikel ${output.itemId} nicht gefunden.`} />;
  }
  const { item } = output;
  return (
    <Card>
      <CardHead
        title={item.name}
        meta={<span className="eos-asst-mono">{item.id}</span>}
      />
      <dl className="eos-asst-rc-kvs">
        <Kv label="Hersteller" value={item.manufacturer ?? '—'} />
        <Kv label="Warengruppe" value={item.itemGroup ?? '—'} />
        <Kv
          label="Preis"
          value={<span className="eos-asst-mono">{fmtEUR(item.price)}</span>}
        />
        <Kv
          label="Ø-Einkaufspreis"
          value={
            <span className="eos-asst-mono">
              {fmtEUR(item.movingAveragePrice)}
            </span>
          }
        />
        <Kv
          label="Lagerbestand"
          value={
            <span className="eos-asst-mono">
              {fmtInt(item.quantityOnStock)}
            </span>
          }
        />
        <Kv
          label="Verfügbar"
          value={
            <span className="eos-asst-mono">
              {fmtInt(item.availableQuantity)}
            </span>
          }
        />
        <Kv
          label="Kundenbestellungen"
          value={
            <span className="eos-asst-mono">
              {fmtInt(item.quantityOrderedByCustomers)}
            </span>
          }
        />
        <Kv
          label="Barcode"
          value={
            item.barCode ? (
              <span className="eos-asst-mono">{item.barCode}</span>
            ) : (
              '—'
            )
          }
        />
      </dl>
    </Card>
  );
}

/** One partner row — shared by every queryBusinessPartners result card. */
function renderPartnerRow(partner: AssistantPartnerSummary): ReactNode {
  const { overdueRatio, avgOrderIntervalDays } = partner;
  const favicon = faviconUrl(partner.website);
  return (
    <li key={partner.id} className="eos-asst-rc-row">
      <span className="eos-asst-rc-row-main">
        <span className="eos-asst-rc-row-namerow">
          {favicon && (
            <img
              src={favicon}
              alt=""
              className="eos-asst-rc-favicon"
              width={14}
              height={14}
              loading="lazy"
            />
          )}
          <span className="eos-asst-rc-row-name">{partner.name}</span>
        </span>
        <span className="eos-asst-rc-row-sub">
          <span className="eos-asst-mono">{partner.id}</span>
          {` · ${partnerTypeLabel(partner.type)}`}
          {` · ${fmtInt(partner.totalOrders)} Bestellungen`}
          {avgOrderIntervalDays != null
            ? ` · Ø ${fmtInt(Math.round(avgOrderIntervalDays))} T`
            : ''}
          {partner.openOrders > 0
            ? ` · ${fmtInt(partner.openOrders)} offen`
            : ''}
        </span>
      </span>
      <span className="eos-asst-rc-row-side">
        <span className="eos-asst-rc-row-side-top">
          {overdueRatio != null && overdueRatio >= OVERDUE_PILL_MIN && (
            <OverduePill ratio={overdueRatio} />
          )}
          <span className="eos-asst-mono">{fmtEUR(partner.revenue)}</span>
        </span>
        <span
          className="eos-asst-rc-row-meta"
          title={
            partner.lastOrderDate
              ? fmtDate(partner.lastOrderDate)
              : undefined
          }
        >
          {fmtRelDays(partner.daysSinceLastOrder)}
        </span>
      </span>
    </li>
  );
}

function PartnerQueryCard({
  output,
}: {
  output: QueryBusinessPartnersOutput;
}) {
  if (output.count === 0) {
    return <Missing text="Keine Geschäftspartner gefunden." />;
  }
  return (
    <Card>
      <CardHead
        title={`${output.count} Geschäftspartner`}
        meta={
          output.totalMatched > output.count
            ? `Top ${output.count} von ${fmtInt(output.totalMatched)}`
            : undefined
        }
      />
      <CollapsibleList
        items={output.businessPartners}
        showLabel={(n) => `Alle ${fmtInt(n)} Geschäftspartner anzeigen`}
        hideLabel={(n) => `${fmtInt(n)} Geschäftspartner ausblenden`}
        renderItem={renderPartnerRow}
      />
    </Card>
  );
}

function PartnerDetailCard({
  output,
}: {
  output: GetBusinessPartnerOutput;
}) {
  if (!output.found) {
    return (
      <Missing
        text={`Geschäftspartner ${output.businessPartnerId} nicht gefunden.`}
      />
    );
  }
  const { businessPartner: partner } = output;
  return (
    <Card>
      <CardHead
        title={partner.legalName || partner.name}
        meta={
          <span className="eos-asst-mono">
            {partner.id} · {partnerTypeLabel(partner.type)}
          </span>
        }
      />
      <dl className="eos-asst-rc-kvs">
        <Kv label="Name" value={partner.name} />
        <Kv
          label="USt-IdNr."
          value={
            partner.vatId ? (
              <span className="eos-asst-mono">{partner.vatId}</span>
            ) : (
              '—'
            )
          }
        />
        <Kv
          label="E-Mail"
          value={
            partner.email ? (
              <a href={`mailto:${partner.email}`}>{partner.email}</a>
            ) : (
              '—'
            )
          }
        />
        <Kv
          label="Website"
          value={
            partner.website ? (
              <a
                href={websiteHref(partner.website)}
                target="_blank"
                rel="noreferrer"
              >
                {partner.website}
              </a>
            ) : (
              '—'
            )
          }
        />
        <Kv
          label="Kreditlimit"
          value={
            <span className="eos-asst-mono">
              {fmtEUR(partner.creditLimit)}
            </span>
          }
        />
        <Kv
          label="Ansprechpartner"
          value={partner.defaultContactPersonName ?? '—'}
        />
      </dl>
    </Card>
  );
}

function OrderListCard({
  output,
}: {
  output: GetBusinessPartnerOrdersOutput;
}) {
  if (!output.found) {
    return (
      <Missing
        text={`Geschäftspartner ${output.businessPartnerId} nicht gefunden.`}
      />
    );
  }
  if (output.orders.length === 0) {
    return <Missing text="Keine Bestellungen gefunden." />;
  }
  return (
    <Card>
      <CardHead
        title={`${output.totalOrders} Bestellungen`}
        meta={
          <span className="eos-asst-mono">
            Partner {output.businessPartnerId}
          </span>
        }
      />
      <CollapsibleList
        items={output.orders}
        showLabel={(n) => `Alle ${fmtInt(n)} ${n === 1 ? 'Bestellung' : 'Bestellungen'} anzeigen`}
        hideLabel={(n) => `${fmtInt(n)} ${n === 1 ? 'Bestellung' : 'Bestellungen'} ausblenden`}
        renderItem={(order) => (
          <li key={order.docNum} className="eos-asst-rc-row">
            <span className="eos-asst-rc-row-main">
              <span className="eos-asst-rc-row-name eos-asst-mono">
                #{order.docNum}
              </span>
              <span className="eos-asst-rc-row-sub">
                {fmtDate(order.date)} · {fmtInt(order.lineCount)} Pos.
                {order.reference ? ` · ${order.reference}` : ''}
              </span>
              {order.items && (
                <span className="eos-asst-rc-row-sub">{order.items}</span>
              )}
            </span>
            <span className="eos-asst-rc-row-side">
              <span className="eos-asst-mono">{fmtEUR(order.total)}</span>
              <StatusPill status={order.status} />
            </span>
          </li>
        )}
      />
    </Card>
  );
}

function OrderDetailCard({ output }: { output: GetOrderOutput }) {
  if (!output.found) {
    return <Missing text={`Auftrag ${output.docNum} nicht gefunden.`} />;
  }
  const { order } = output;
  return (
    <Card>
      <div className="eos-asst-rc-head">
        <span className="eos-asst-rc-title eos-asst-mono">
          Auftrag #{order.docNum}
        </span>
        <StatusPill status={order.status} />
      </div>
      <dl className="eos-asst-rc-kvs">
        <Kv
          label="Geschäftspartner"
          value={`${order.businessPartnerName} (${order.businessPartnerId})`}
        />
        <Kv label="Datum" value={fmtDate(order.date)} />
        <Kv label="Fälligkeit" value={fmtDate(order.dueDate)} />
        {order.reference && (
          <Kv label="Referenz" value={order.reference} />
        )}
        <Kv
          label="MwSt."
          value={<span className="eos-asst-mono">{fmtEUR(order.vat)}</span>}
        />
        <Kv
          label="Gesamt"
          value={
            <span className="eos-asst-mono">{fmtEUR(order.total)}</span>
          }
        />
      </dl>
      <ul className="eos-asst-rc-lines">
        {order.lineItems.map((line) => (
          <li key={line.lineNum} className="eos-asst-rc-line">
            <span className="eos-asst-rc-row-main">
              <span className="eos-asst-rc-row-name">{line.description}</span>
              <span className="eos-asst-rc-row-sub">
                <span className="eos-asst-mono">{line.itemCode}</span>
                {` · ${fmtInt(line.quantity)} × ${fmtEUR(line.unitPrice)}`}
                {line.openQuantity != null && line.openQuantity > 0
                  ? ` · ${fmtInt(line.openQuantity)} offen`
                  : ''}
              </span>
            </span>
            <span className="eos-asst-mono eos-asst-rc-line-total">
              {fmtEUR(line.lineTotal)}
            </span>
          </li>
        ))}
      </ul>
      {order.comments && (
        <div className="eos-asst-rc-note">{order.comments}</div>
      )}
    </Card>
  );
}

/** Human label for the SAP `gender` enum value. */
function genderLabel(gender: string | null): string {
  switch (gender) {
    case 'gt_Male':
      return 'männlich';
    case 'gt_Female':
      return 'weiblich';
    case 'gt_Undefined':
    case 'gt_Masked':
      return 'k. A.';
    default:
      return '—';
  }
}

/** Human label for the SAP `U_Anrede` code. */
function salutationLabel(salutation: string | null): string {
  switch (salutation) {
    case 'D':
      return 'Du';
    case 'S':
      return 'Sie';
    default:
      return '—';
  }
}

function ContactListCard({
  output,
}: {
  output: ListBusinessPartnerContactsOutput;
}) {
  if (!output.found) {
    return (
      <Missing
        text={`Geschäftspartner ${output.businessPartnerId} nicht gefunden.`}
      />
    );
  }
  if (output.contacts.length === 0) {
    return <Missing text="Keine Ansprechpartner hinterlegt." />;
  }
  return (
    <Card>
      <CardHead
        title={`${output.totalContacts} Ansprechpartner`}
        meta={
          <span className="eos-asst-mono">
            Partner {output.businessPartnerId}
          </span>
        }
      />
      <CollapsibleList
        items={output.contacts}
        showLabel={(n) => `Alle ${fmtInt(n)} Ansprechpartner anzeigen`}
        hideLabel={(n) => `${fmtInt(n)} Ansprechpartner ausblenden`}
        renderItem={(contact) => (
          <li key={contact.id} className="eos-asst-rc-row">
            <span className="eos-asst-rc-row-main">
              <span className="eos-asst-rc-row-name">{contact.name}</span>
              <span className="eos-asst-rc-row-sub">
                <span className="eos-asst-mono">{contact.id}</span>
                {contact.position ? ` · ${contact.position}` : ''}
                {` · Anrede: ${salutationLabel(contact.salutation)}`}
                {` · ${genderLabel(contact.gender)}`}
              </span>
              {(contact.email || contact.phone1) && (
                <span className="eos-asst-rc-row-sub">
                  {contact.email && (
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  )}
                  {contact.email && contact.phone1 ? ' · ' : ''}
                  {contact.phone1 && (
                    <span className="eos-asst-mono">{contact.phone1}</span>
                  )}
                </span>
              )}
            </span>
          </li>
        )}
      />
    </Card>
  );
}

/**
 * Renders the result card for a finished tool part. Returns `null` until the
 * tool has produced output — the panel shows the activity card until then.
 */
export function AssistantToolResult({ part }: { part: ResultPart }) {
  if (part.state !== 'output-available') return null;
  switch (part.type) {
    case 'tool-searchItems':
      return <ItemListCard output={part.output} />;
    case 'tool-getItem':
      return <ItemDetailCard output={part.output} />;
    case 'tool-queryBusinessPartners':
      return <PartnerQueryCard output={part.output} />;
    case 'tool-getBusinessPartner':
      return <PartnerDetailCard output={part.output} />;
    case 'tool-getBusinessPartnerOrders':
      return <OrderListCard output={part.output} />;
    case 'tool-getOrder':
      return <OrderDetailCard output={part.output} />;
    case 'tool-listBusinessPartnerContacts':
      return <ContactListCard output={part.output} />;
    default:
      return null;
  }
}
